import { Box, LinearProgress, Typography, Chip } from '@mui/material';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState, createRef } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import styled from 'styled-components';
import '../../css/video-react.css';
import { isPlaying } from '../../utils';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import { timeToSeconds } from '../../utils/formatUtils';
import EditTrackingService from '../../services/EditTrackingService';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import StarIcon from '@mui/icons-material/Star';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

const Style = styled.div`
    display: flex;
    justify-content: center;
    height: 100%;
    width: 100%;
    align-items: center;
    flex-direction: column;
    background-color: #000;
    
    .player-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        height: 100%;
        position: relative;
        overflow: hidden;
    }
    
    .videoPlayer {
        width: 100%;
        height: auto;
        max-height: calc(100vh - 350px);
        object-fit: contain;
        background-color: #000;
        max-width: 70%; /* Default for all horizontal videos */
        margin: 0 auto;
        
        /* Responsive width adjustments */
        @media (min-width: 1600px) {
            max-width: 60%; /* Even smaller for very large screens */
        }
        
        @media (max-width: 1200px) {
            max-width: 80%; /* Slightly larger for medium screens */
        }
        
        @media (max-width: 768px) {
            max-width: 95%; /* Nearly full width for small screens */
        }
    }
    
    /* Add specific styles for vertical videos */
    .vertical-video .videoPlayer {
        max-width: 25% !important; /* Make video even smaller (from 35% to 30%) */
        max-height: calc(100vh - 550px) !important; /* Add even more space below and above */
        margin: 40px auto 0 !important; /* Add top margin to ensure info is visible */
    }
    
    /* Make video info always visible in vertical mode */
    .vertical-video .videoInfo {
        position: fixed !important; /* Keep fixed at top */
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        z-index: 2000 !important; /* Ensure it's above everything */
        background-color: rgba(0, 0, 0, 0.85) !important; /* More opaque background */
        padding: 8px 0 !important;
        font-size: 12px !important;
        border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
    }
    
    /* Fix controls for vertical videos */
    .vertical-video .controls {
        max-width: 70% !important; /* Wider than the video but not too wide */
        margin: 10px auto !important; /* Increase top margin for more separation */
        padding: 5px 0 !important;
        background-color: rgba(0, 0, 0, 0.7) !important;
        position: relative !important; /* Ensure position is relative not absolute */
        z-index: 10 !important; /* Lower than subtitle z-index */
    }
    
    /* Improve subtitle positioning for vertical videos */
    .vertical-video .subtitle-container {
        position: relative !important; /* Change from absolute to relative */
        bottom: auto !important; /* Remove bottom positioning */
        margin-top: 10px !important; /* Add margin above instead of bottom positioning */
        width: 100% !important;
        z-index: 20 !important; /* Higher than controls */
    }

    .controls {
        width: 100%;
        gap: 5px;
        display: flex;
        justify-content: center;
        padding: 5px;
        flex-wrap: wrap;
        background-color: #000;
    }
    
    .controls button {
        background: #fff;
        border: none;
        padding: 8px 12px;
        border-radius: 5px;
        cursor: pointer;
        font-size: 13px;
        transition: background 0.3s;
        margin: 2px;
    }
    
    .controls button:hover {
        background: #ddd;
    }
    
    .controls button.active {
        background: #007bff;
        color: #fff;
    }
    
    .controls i {
        font-size: 14px;
    }

    .subtitle-container {
        width: 100%;
        display: flex;
        justify-content: center;
        position: absolute;
        bottom: 5%;
        z-index: 1000;
    }
    
    .subtitle, .subtitle-fallback {
        background-color: rgba(0, 0, 0, 0.8);
        border-radius: 4px;
        min-width: 60%; /* Added minimum width */
        max-width: 90%; /* Increased maximum width */
    }
    
    .textarea {
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
        background-color: transparent;
        color: white;
        border: none;
        text-align: center;
        resize: none;
        min-width: 400px; /* Added minimum width */
        width: 100%;
    }
`;

const VideoContainer = styled.div`
    position: relative;
    width: 100%;
    max-width: 900px;
    height: auto;
    margin: 0 auto;
    background-color: #000;
    
    video {
        width: 100%;
        height: auto;
        max-height: calc(100vh - 300px);
        object-fit: contain;
        background-color: #000;
    }
    
    .subtitle {
        position: absolute;
        bottom: 25%;
        width: 100%;
        text-align: center;
        z-index: 5;
    }
    
    textarea {
        width: 90%;
        max-width: 500px;
        margin: 0 auto;
        background-color: rgba(0, 0, 0, 0.7);
        color: white;
        border: none;
        padding: 10px;
        text-align: center;
        resize: none;
    }
`;

const PlayerContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    
    video {
        max-width: 100%;
        max-height: 200px; /* Limit video height */
        object-fit: contain;
    }
    
    .player-controls {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 10px;
    }
`;

const VideoWrap = memo(
    ({ setPlayer, setCurrentTime, setPlaying }) => {
        const $video = useRef(null);
        const isManualUpdateRef = useRef(false);
        const wasPlayingBeforeSeekRef = React.useRef(false);
        const activeSeekListenersRef = useRef([]);

        // Update the first useEffect with better error handling and loading logic
        useEffect(() => {
            const videoProps = JSON.parse(localStorage.getItem('videoProps'));
            if (!videoProps || !videoProps.compressedUrl) {
                console.error("No video properties found in localStorage");
                return;
            }
            
            console.log("Loading video from URL:", videoProps.compressedUrl);
            
            // Define the event handler functions outside of the forEach
            // so we can reference them for removal
            const debugEventHandler = (eventName) => () => {
                console.log(`Video event: ${eventName}, readyState: ${$video.current?.readyState}`);
            };
            
            const errorHandler = () => {
                console.error("Video error:", $video.current.error);
            };
            
            // Store references to the handlers for each event
            const eventHandlers = {};
            
            // Fetch with timeout and error handling
            const fetchTimeout = setTimeout(() => {
                console.warn("Video fetch timeout - trying direct src set");
                if ($video.current) {
                    $video.current.src = videoProps.compressedUrl;
                }
            }, 5000);
            
            fetch(videoProps.compressedUrl, {
                headers: {
                    Range: 'bytes=0-2046',
                },
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                clearTimeout(fetchTimeout);
                return response.blob();
            })
            .then(blob => {
                if ($video.current) {
                    $video.current.src = videoProps.compressedUrl;
                    $video.current.playbackRate = 1;
                    //console.log("Video source set successfully");
                }
            })
            .catch(error => {
                console.error("Error fetching video:", error);
                clearTimeout(fetchTimeout);
                // Still try to set the src directly as fallback
                if ($video.current) {
                    $video.current.src = videoProps.compressedUrl;
                }
            });
            
            return () => {
                clearTimeout(fetchTimeout);
                
            };
        }, []); // Empty dependency array ensures this runs only on mount

        // Update the updateVideoTime function with this improved version
        const updateVideoTime = (time) => {
            if (!$video.current) {
                console.warn("Video element not available for time update");
                return;
            }
                        
            // Flag that we're manually updating to prevent RAFs loop updates
            isManualUpdateRef.current = true;
            console.log("Updating video time to:", time);
            // Helper function to perform the actual time update
            function performTimeUpdate(seekTime) {
                try {
                    isManualUpdateRef.current = true;
                    
                    // Save the current paused state
                    const wasPaused = $video.current.paused;
                    
                    // Set the time
                    $video.current.currentTime = seekTime;
                    
                    // Add a seeked event listener
                    const onSeeked = () => {
                        
                        // Clean up the listener immediately to prevent memory leaks
                        $video.current.removeEventListener('seeked', onSeeked);
                        clearTimeout(timeoutId); // Clear the timeout since we succeeded
                        
                        // Force a refresh of the video frame, but only if already paused
                        if (wasPaused) {
                            // Force a frame update without actually playing the video
                            // This is a more reliable approach that keeps the video paused
                            
                            // Method 1: Use requestVideoFrameCallback if available (modern browsers)
                            if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
                                $video.current.requestVideoFrameCallback(() => {
                            });
                            } 
                            // Method 2: Use the play/pause trick but with better guarantees
                            else {
                                // Mark that we're doing a forced refresh
                                const doingForcedRefresh = true;
                                
                                // Play for a tiny moment then pause
                                const playPromise = $video.current.play();
                                
                                if (playPromise !== undefined) {
                                    playPromise.then(() => {
                                        // Immediately pause the video
                                        setTimeout(() => {
                                            if (doingForcedRefresh) {
                                                $video.current.pause();
                                                console.log("Video paused after forced frame update");
                                            }
                                        }, 30); // Use a shorter timeout
                                    }).catch(err => {
                                        console.error("Error during frame update:", err);
                                        // Make sure it stays paused on error
                                        $video.current.pause();
                                    });
                                } else {
                                    // For browsers that don't return a promise
                                    setTimeout(() => {
                                        $video.current.pause();
                                    }, 30);
                                }
                            }
                        }
                        
                        // Reset the flag after a delay to allow frame updates
                        setTimeout(() => {
                            isManualUpdateRef.current = false;
                        }, 100);
                    };
                    
                    // Listen for the seeked event
                    $video.current.addEventListener('seeked', onSeeked);
                    
                    // Set a backup timeout in case the seeked event doesn't fire
                    const timeoutId = setTimeout(() => {
                        // Clean up the listener to prevent memory leaks
                        $video.current.removeEventListener('seeked', onSeeked);
                        console.log("Seek timeout reached, current time:", $video.current.currentTime);
                        // Rest of your timeout handling code...
                    }, 1000);
                    
                    // Also add a component unmount safety by storing references in a ref
                    // This is a bit advanced, but would be good for a production app
                    if (!activeSeekListenersRef.current) {
                        activeSeekListenersRef.current = [];
                    }
                    
                    // Store cleanup functions that can be called on unmount
                    activeSeekListenersRef.current.push(() => {
                        $video.current?.removeEventListener('seeked', onSeeked);
                        clearTimeout(timeoutId);
                    });
                } catch (error) {
                    console.error("Error updating video time:", error);
                    isManualUpdateRef.current = false;
                }
            }
            
            // Check if video is in a state where seeking is possible
            if ($video.current.readyState < 2) { // HAVE_CURRENT_DATA or higher needed for reliable seeking
                console.warn(`Video not ready for seeking. ReadyState: ${$video.current.readyState}`);
                
                // Add an event listener to set time once video is ready
                const onCanSeek = () => {
                    performTimeUpdate(time);
                    $video.current.removeEventListener('canplay', onCanSeek);
                };
                
                $video.current.addEventListener('canplay', onCanSeek);
                return;
            }
            
            // Video is ready, perform the seek
            performTimeUpdate(time);
        };
        
        useEffect(() => {
            let prevPlaying, prevTime;
            
            
            
            // Expose this function to parent components
            setPlayer({
                element: $video.current,
                updateTime: updateVideoTime
            });
            
            (function loop() {
                window.requestAnimationFrame(() => {
                    if ($video.current) {
                        const currentPlaying = isPlaying($video.current);
                        const currentTime = $video.current.currentTime || 0;
                        
                        if (prevPlaying !== currentPlaying) {
                            setPlaying(currentPlaying);
                            prevPlaying = currentPlaying;
                        }
                        
                        // Only update state time if it's not a manual update - use the ref!
                        if (prevTime !== currentTime && !isManualUpdateRef.current) {
                            setCurrentTime(currentTime);
                            prevTime = currentTime;
                        }
                    }
                    loop();
                });
            })();
        }, [setPlayer, setCurrentTime, setPlaying]);
        
        // Update the VideoWrap component's implementation

        // First, add event listeners for play and pause events to track state
        useEffect(() => {
            const videoElement = $video.current;
            if (!videoElement) return;
            
            const handlePlay = () => {
                console.log("Native play event detected");
                setPlaying(true);
            };
            
            const handlePause = () => {
                console.log("Native pause event detected");
                setPlaying(false);
            };
            
            // Add event listeners for native controls
            videoElement.addEventListener('play', handlePlay);
            videoElement.addEventListener('pause', handlePause);
            
            return () => {
                // Clean up event listeners
                videoElement.removeEventListener('play', handlePlay);
                videoElement.removeEventListener('pause', handlePause);
            };
        }, [$video, setPlaying]);


        const onLoadStarted = useCallback(() => {
            if ($video.current) {
                if ($video.current) {
                    var splashScreen = document.getElementById('splash-screen');
                    splashScreen.style.display = 'block';
                }
            }
        }, [$video]);

        const onCanPlay = useCallback(() => {
            if ($video.current) {
                var splashScreen = document.getElementById('splash-screen');
                splashScreen.style.display = 'none';
            }
        }, [$video]);

        const handlePlaybackSpeed = useCallback((speed) => {
            if ($video.current) {
                $video.current.playbackRate = speed;
            }
        }, []);

        const handleForward = useCallback(() => {
            if ($video.current) {
                $video.current.currentTime += 10; // Forward 10 seconds
            }
        }, []);

        const handleRewind = useCallback(() => {
            if ($video.current) {
                $video.current.currentTime -= 10; // Rewind 10 seconds
            }
        }, []);

        const getButtonStyle = (speed) => ({
            backgroundColor: $video.current && $video.current.playbackRate === speed ? '#007bff' : '#fff',
            color: $video.current && $video.current.playbackRate === speed ? '#fff' : '#000',
            border: 'none',
            padding: '10px 15px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'background 0.3s',
        });

        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        const selectedLanguage = JSON.parse(localStorage.getItem('setSelectedLng'));
        //console.log(`${videoProps.compressedUrl}`);
        const videoName = videoProps.videoName;
        const uiLang = localStorage.getItem('UILanguage');
        
        const info = 'Video Name: ' + videoName;
        window.AudioContext = window.AudioContext || window.webkitAudioContext;
        navigator.getUserMedia =
            navigator.getUserMedia ||
            navigator.webkitGetUserMedia ||
            navigator.mozGetUserMedia ||
            navigator.msGetUserMedia;
        return (
            <>
                {/* Video element */}
                <PlayerContainer>
                    <video
                        crossOrigin="anonymous"
                        id="videoPlayer"
                        className="videoPlayer"
                        src={`${videoProps.compressedUrl}`}
                        ref={$video}
                        controls
                        onLoadStart={onLoadStarted}
                        onCanPlay={onCanPlay}
                        preload="auto"
                        playsInline
                        style={{
                            height: 'auto',
                            width: '100%',
                            maxWidth: '70%', // Change from 80% to 70%
                            maxHeight: 'calc(100vh - 300px)',
                            objectFit: 'contain',
                            margin: '0 auto',
                            display: 'block',
                            backgroundColor: '#000'
                        }}
                    ></video>
                </PlayerContainer>

                {/* Video info */}
                <p className="videoInfo" align="center" style={{ 
                    width: '100%', 
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    margin: '0',
                    padding: '5px 0',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    fontSize: '12px',
                    zIndex: 1000, // Add z-index to ensure visibility
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)' // Add subtle border
                }}>
                    {info}
                </p>

                {/* Controls */}
                <div className="controls" style={{
                    padding: '5px 0',
                    width: '100%',
                    maxWidth: '900px',
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    margin: '10px auto',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: '5px'
                }}>
                    <button style={getButtonStyle(0.5)} onClick={() => handlePlaybackSpeed(0.5)}>
                        0.5x
                    </button>
                    <button style={getButtonStyle(1)} onClick={() => handlePlaybackSpeed(1)}>
                        1x
                    </button>
                    <button style={getButtonStyle(1.25)} onClick={() => handlePlaybackSpeed(1.25)}>
                        1.25x
                    </button>
                    <button style={getButtonStyle(1.5)} onClick={() => handlePlaybackSpeed(1.5)}>
                        1.5x
                    </button>
                    <button style={getButtonStyle(2)} onClick={() => handlePlaybackSpeed(2)}>
                        2x
                    </button>
                    <button onClick={handleRewind}>
                        <i className="fas fa-undo-alt"></i>
                    </button>
                    <button onClick={handleForward}>
                        <i className="fas fa-redo-alt"></i>
                    </button>
                </div>
            </>
        );
    },
    () => true,
);

export default function Players(props) {
    const [currentSub, setCurrentSub] = useState(null);
    const [, setFocusing] = useState(false);
    const [, setInputItemCursor] = useState(0);
    const $player = createRef();
    // Add this line to define the missing ref
    const $video = useRef(null);
    const [editingStats, setEditingStats] = useState(null);
    const resizeTimeoutRef = useRef(null);

    const determineUserLevel = useCallback((videoProps, selectedLng) => {
        if (!videoProps || !selectedLng) return '';
        
        // Find the selected language in videoProps
        const selectedLanguage = videoProps.videoLanguages.find(
            lang => lang.languageName === selectedLng
        );
        
        if (!selectedLanguage) return 'Level 1'; // Default if language not found
        
        // Determine level based on the subtitle progress state
        switch (selectedLanguage.processStage) {
            case 'LEVEL0_AI_COMPLETE':
                return 'Level 1'; // First level editing AI-generated subtitles
            case 'LEVEL2_WIP':
            case 'LEVEL1_SEVAK_COMPLETE':
                return 'Level 2'; // Second level editing after Level 1 is complete
            case 'LEVEL3_WIP':
            case 'LEVEL3_SEVAK_COMPLETE':
                return 'Level 3'; // If you have a third level
            default:
                return 'Level 1'; // Default level
        }
    }, []);

    // Add effect to fetch edit tracking stats
    useEffect(() => {
        let isMounted = true;
        let intervalId = null;
        
        const fetchEditingStats = async () => {
            try {
                // Get the video ID and language from props or localStorage
                const videoProps = JSON.parse(localStorage.getItem('videoProps'));
                const videoId = props.videoId || 
                    (videoProps ? videoProps.id : null);
                
                const language = props.selectedLng; // This is the currently selected language
                
                if (videoId && language && isMounted) {
                    // Get language-specific stats from the tracking service
                    const currentLevel = determineUserLevel(videoProps, language);
                    const stats = await EditTrackingService.getVideoLanguageEditStats(videoId, language);
                    
                    // Only update state if component is still mounted
                    if (isMounted && stats) {
                        // Add currentLevel to stats
                        stats.currentLevel = currentLevel;
                        console.log("Fetched editing stats:", stats);
                        
                        setEditingStats(stats);
                    }
                }
            } catch (error) {
                console.error("Error fetching editing stats:", error);
            }
        };
        
        // Initial fetch
        fetchEditingStats();
        
        // Set up interval to refresh stats periodically
        intervalId = setInterval(fetchEditingStats, 60000); // Refresh every minute
        
        return () => {
            isMounted = false;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [props.videoId, props.selectedLng, determineUserLevel]);

    // Helper function to format time (reuse existing one if available)
    const formatTime = (milliseconds) => {
        if (!milliseconds) return "00:00:00";
        
        let seconds = Math.floor(milliseconds / 1000);
        let minutes = Math.floor(seconds / 60);
        let hours = Math.floor(minutes / 60);
        
        seconds = seconds % 60;
        minutes = minutes % 60;
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // Add these state variables
    const [sessionTime, setSessionTime] = useState(0);
    const [lastRefresh, setLastRefresh] = useState(Date.now());
    const uiTimerIntervalRef = useRef(null);
    const statsRefreshIntervalRef = useRef(null);

    // Add this useEffect to continuously update the timer display
    useEffect(() => {
        
        
        // Set up a timer to update the UI display every second (local only, no server calls)
        uiTimerIntervalRef.current = setInterval(() => {
            // Update the session time counter (increases by 1 second)
            setSessionTime(prev => prev + 1000);
        }, 1000);
        
        return () => {
            // Clean up both intervals on unmount
            if (uiTimerIntervalRef.current) {
                clearInterval(uiTimerIntervalRef.current);
            }
            
        };
    }, []);

    useEffect(() => {
        const findVideoElement = () => document.querySelector('#videoPlayer');
        const videoElement = findVideoElement();
        
        if (!videoElement) return;

        if (props.subtitle && 
            Array.isArray(props.subtitle) && 
            props.currentIndex >= 0 && 
            props.currentIndex < props.subtitle.length) {
            
            const currentSubItem = props.subtitle[props.currentIndex];
            //console.log("Current subtitle item:", currentSubItem);
            //console.log("Current playback time:", videoElement.currentTime);
            // Check if current playback time is within the time range of this subtitle
            // If not, show blank (this handles the gap case)
            const startTimeSec = timeToSeconds(currentSubItem.start);
            const endTimeSec = timeToSeconds(currentSubItem.end);

            if (videoElement.currentTime >= startTimeSec && 
                videoElement.currentTime <= endTimeSec) {
                setCurrentSub(currentSubItem);
                //console.log("Current subtitle set:", currentSubItem);
                //console.log("Current time:", videoElement.currentTime, "Subtitle start:", startTimeSec, "Subtitle end:", endTimeSec);
            } else {
                // Current time doesn't match the subtitle's time range - show blank
                //console.log("Current time does not match subtitle time range - clearing currentSub");
                //console.log("Current time:", videoElement.currentTime, "Subtitle start:", startTimeSec, "Subtitle end:", endTimeSec);
                setCurrentSub(null);
            }
        } else {
            // No valid subtitle at current index - clear the display
            //console.log("No valid subtitle at current index - clearing currentSub");
            setCurrentSub(null);
        }
    }, [props.subtitle, props.currentIndex, props.currentTime]);

    const onChangeEng = useCallback(
        (event) => {
            props.player.element.pause();
            props.updateSub(currentSub, { text: event.target.value });
            if (event.target.selectionStart) {
                setInputItemCursor(event.target.selectionStart);
            }
        },
        [props, currentSub],
    );

    const onClick = useCallback(
        (event) => {
            props.player.element.pause();
            if (event.target.selectionStart) {
                setInputItemCursor(event.target.selectionStart);
            }
        },
        [props],
    );

    const onFocus = useCallback((event) => {
        setFocusing(true);
        if (event.target.selectionStart) {
            setInputItemCursor(event.target.selectionStart);
        }
    }, []);

    const onBlur = useCallback(() => {
        setTimeout(() => setFocusing(false), 500);
    }, []);

    // Update the video element styles to handle vertical videos properly
    // First, add a useEffect to detect video orientation after it loads
    useEffect(() => {
        // We can't directly access the video element from this component
        // since it's in the child VideoWrap component
        // Let's create a function to find the video element instead
        const findVideoElement = () => document.querySelector('#videoPlayer');
        const videoElement = findVideoElement();
        
        if (!videoElement) return;

        const handleVideoMetadata = () => {
            const video = videoElement;
            if (!video) return;

            // Check if video is vertical (height > width)
            const isVertical = video.videoHeight > video.videoWidth;
            
            // Find the main player container
            const playerContainer = document.querySelector('.player-container');
            
            if (isVertical) {
                console.log("Vertical video detected - applying special styles");
                
                // Apply special styles for vertical videos - make video even smaller
                video.style.maxHeight = 'calc(100vh - 550px)';
                video.style.maxWidth = '25%'; // Make video even smaller (from 35% to 30%)
                video.style.margin = '40px auto 0'; // Add top margin to make room for video info
                
                // Add vertical-video class to the player container for CSS targeting
                if (playerContainer) {
                    playerContainer.classList.add('vertical-video');
                }
                
                // Fix video info positioning
                const videoInfo = document.querySelector('.videoInfo');
                if (videoInfo) {
                    videoInfo.style.position = 'absolute';
                    videoInfo.style.top = '0';
                    videoInfo.style.left = '0';
                    videoInfo.style.width = '100%';
                    videoInfo.style.zIndex = '2000';
                    videoInfo.style.backgroundColor = 'rgba(0, 0, 0, 0.85)';
                    videoInfo.style.padding = '8px 0';
                    videoInfo.style.fontSize = '12px';
                    videoInfo.style.borderBottom = '1px solid rgba(255, 255, 255, 0.2)';
                }
                
                // Ensure controls are visible with better positioning
                const controls = document.querySelector('.controls');
                if (controls) {
                    controls.style.maxWidth = '70%';
                    controls.style.margin = '10px auto';
                    controls.style.padding = '10px 0';
                    controls.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                    controls.style.borderRadius = '5px';
                    controls.style.position = 'relative';
                    controls.style.zIndex = '10';
                }
                
                // Adjust subtitle container - move below controls
                const subtitleContainer = document.querySelector('.subtitle-container');
                if (subtitleContainer) {
                    subtitleContainer.style.position = 'relative';
                    subtitleContainer.style.bottom = 'auto';
                    subtitleContainer.style.marginTop = '5px';
                    subtitleContainer.style.width = '100%';
                    subtitleContainer.style.zIndex = '20';
                }
                
                // Also adjust subtitle elements if they exist
                const subtitle = document.querySelector('.subtitle');
                if (subtitle) {
                    subtitle.style.maxWidth = '80%';
                    subtitle.style.minWidth = '60%';
                    subtitle.style.marginTop = '0';
                    subtitle.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
                }
                
                const subtitleFallback = document.querySelector('.subtitle-fallback');
                if (subtitleFallback) {
                    subtitleFallback.style.maxWidth = '80%';
                    subtitleFallback.style.minWidth = '60%';
                    subtitleFallback.style.marginTop = '0';
                }
            } else {
                // Reset styles for horizontal videos with max-width constraint
                video.style.maxHeight = 'calc(100vh - 350px)';
                video.style.maxWidth = '70%'; // Change from 100% to 70% to reduce width
                video.style.margin = '0 auto';

                if (playerContainer) {
                    playerContainer.classList.remove('vertical-video');
                }
                
                // Reset controls with adjusted width to match video
                const controls = document.querySelector('.controls');
                if (controls) {
                    controls.style.maxWidth = '70%'; // Adjust control width to match video
                    controls.style.margin = '5px auto';
                    controls.style.padding = '10px 0';
                }
                
                // Reset subtitle container
                const subtitleContainer = document.querySelector('.subtitle-container');
                if (subtitleContainer) {
                    subtitleContainer.style.bottom = '5%';
                    subtitleContainer.style.width = '100%';
                }
                
                // Reset subtitle elements
                const subtitle = document.querySelector('.subtitle');
                if (subtitle) {
                    subtitle.style.maxWidth = '90%';
                    subtitle.style.minWidth = '60%';
                    subtitle.style.marginTop = '15px';
                }
                
                const subtitleFallback = document.querySelector('.subtitle-fallback');
                if (subtitleFallback) {
                    subtitleFallback.style.maxWidth = '90%';
                    subtitleFallback.style.minWidth = '60%';
                    subtitleFallback.style.marginTop = '15px';
                }
            }
        };

        // Initial check when metadata is loaded
        handleVideoMetadata();

        // Add event listener for metadata load
        videoElement.addEventListener('loadedmetadata', handleVideoMetadata);
        
        // Also re-check when video is resized or window size changes
        const resizeObserver = new ResizeObserver((entries) => {
            // Debounce the resize handler to prevent loops
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            
            resizeTimeoutRef.current = setTimeout(() => {
                handleVideoMetadata();
            }, 100); // Add a small delay
        });
        
        // Apply the observer with error handling
        if (videoElement) {
            try {
                resizeObserver.observe(videoElement);
            } catch (error) {
                console.error("Error setting up ResizeObserver:", error);
            }
        }

        // Add window resize listener as backup
        window.addEventListener('resize', handleVideoMetadata);

        return () => {
            const videoElement = findVideoElement();
            if (videoElement) {
                videoElement.removeEventListener('loadedmetadata', handleVideoMetadata);
                try {
                    resizeObserver.disconnect();
                } catch (error) {
                    console.error("Error disconnecting ResizeObserver:", error);
                }
            }
            window.removeEventListener('resize', handleVideoMetadata);
            
            // Clear any pending timeouts
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
        };
    }, []); // Run once on mount

    return (
        <Style>
            <div 
                className="player-container"
                style={{ 
                    width: '100%', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    backgroundColor: '#000',
                    position: 'relative',
                    height: '100%',
                    maxHeight: 'calc(100vh - 100px)',
                    overflow: 'hidden'
                }}
            >
                {/* Splash screen */}
                <div id="splash-screen" style={{ display: 'none', width: '100%' }}>
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress />
                    </Box>
                </div>
                
                {/* Time tracking stats panel - More compact version with smaller fonts and spacing */}
                {editingStats && (
                    <Box 
                        sx={{ 
                            position: 'absolute', 
                            top: 10, 
                            right: 10, 
                            zIndex: 1500, 
                            backgroundColor: 'rgba(20,20,30,0.85)',
                            padding: '5px', // Further reduced padding
                            borderRadius: '8px',
                            color: 'white',
                            maxWidth: '180px', // Reduced max width
                            boxShadow: '0 3px 15px rgba(0,0,0,0.4), 0 0 10px rgba(70,130,180,0.3)',
                            border: '1px solid rgba(80,150,230,0.4)',
                            backdropFilter: 'blur(8px)',
                            transition: 'all 0.3s ease',
                            fontSize: '0.75rem', // Even smaller base font size
                            '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 5px 20px rgba(0,0,0,0.5), 0 0 15px rgba(100,180,255,0.5)'
                            }
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ // Changed to subtitle2 (smaller)
                            borderBottom: '1px solid rgba(100,180,255,0.5)',
                            pb: 0.25, // Reduced padding
                            mb: 0.5, // Reduced margin
                            fontWeight: 'bold',
                            background: 'linear-gradient(90deg, #fff, #64b5f6)',
                            backgroundClip: 'text',
                            textFillColor: 'transparent',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.25, // Smaller gap
                            fontSize: '0.8rem' // Even smaller font size
                        }}>
                            <AccessTimeIcon fontSize="small" sx={{ color: '#64b5f6', fontSize: '0.8rem' }}/>
                            Timer
                        </Typography>
                        
                        {/* Session timer - more compact */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.25, // Reduced gap
                            p: 0.25, // Reduced padding
                            borderRadius: '12px',
                            backgroundColor: 'rgba(100,180,255,0.15)',
                            boxShadow: 'inset 0 0 5px rgba(100,180,255,0.2)',
                            mb: 0.5, // Reduced margin
                        }}>
                            <AccessTimeFilledIcon fontSize="small" sx={{ color: '#64b5f6', fontSize: '0.75rem' }}/>
                            <Typography 
                                variant="body2" // Changed to body2 (smaller)
                                sx={{ 
                                    fontFamily: 'monospace', 
                                    fontWeight: 'bold',
                                    color: '#fff',
                                    textShadow: '0 0 5px rgba(100,180,255,0.8)',
                                    fontSize: '0.75rem' // Smaller font size
                                }}
                            >
                                {EditTrackingService.formatTime(sessionTime)}
                            </Typography>
                        </Box>
                        
                        {/* Total time by level - even more compact */}
                        {editingStats && editingStats.totalTimeByLevel && Object.entries(editingStats.totalTimeByLevel).map(([level, stats]) => (
                            <Box 
                                key={level} 
                                sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    mb: 0.5, // Further reduced margin
                                    p: 0.25, // Further reduced padding
                                    borderRadius: '4px',
                                    backgroundColor: 'rgba(255,255,255,0.08)',
                                    transform: level === (editingStats.currentLevel || '') ? 'scale(1.03)' : 'none',
                                    transition: 'all 0.2s ease',
                                    border: level === (editingStats.currentLevel || '') ? '1px solid rgba(100,180,255,0.5)' : 'none',
                                }}
                            >
                                <Chip 
                                    label={`${level}`} 
                                    size="small"
                                    color={level === 'Level 1' ? 'primary' : 
                                           level === 'Level 2' ? 'secondary' : 
                                           level === 'Level 3' ? 'success' : 'default'}
                                    variant="filled"
                                    sx={{ 
                                        height: '16px', // Further reduced height
                                        fontSize: '9px', // Smaller font
                                        mr: 0.25, // Reduced margin
                                        fontWeight: 'bold',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                        '& .MuiChip-label': { px: 0.5 }, // Further reduced padding
                                        backgroundColor: level === 'Level 1' ? '#64b5f6' : 
                                                        level === 'Level 2' ? '#ba68c8' : 
                                                        level === 'Level 3' ? '#4caf50' : '#9e9e9e',
                                        color: 'white',
                                        borderRadius: '8px',
                                    }}
                                    icon={level === (editingStats.currentLevel || '') ? 
                                        <StarIcon sx={{ fontSize: '0.6rem' }} /> : null}
                                />
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'flex-end' 
                                }}>
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            fontFamily: 'monospace', 
                                            fontWeight: 'bold',
                                            fontSize: '0.65rem' // Smaller font
                                        }}
                                    >
                                        {formatTime(stats)}
                                    </Typography>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={Math.min(100, (stats / (1000 * 60 * 60 * 3)) * 100)} 
                                        sx={{ 
                                            width: '50px', // Smaller width
                                            height: '2px', // Reduced height
                                            mt: 0.15, // Reduced margin
                                            borderRadius: '1px',
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: level === '1' ? '#64b5f6' : 
                                                                level === '2' ? '#ba68c8' : 
                                                                level === '3' ? '#4caf50' : '#fff',
                                            }
                                        }} 
                                    />
                                </Box>
                            </Box>
                        ))}
                        
                        {editingStats.currentSessionTime && (
                            <Box sx={{ 
                                mt: 0.75, // Reduced margin
                                pt: 0.75, // Reduced padding
                                borderTop: '1px solid rgba(100,180,255,0.2)', // Thinner border
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                animation: 'pulse 2s infinite'
                            }}>
                                <Typography variant="caption" sx={{ 
                                    display: 'block',
                                    color: 'rgba(255,255,255,0.7)',
                                    fontStyle: 'italic',
                                    mb: 0.25, // Reduced margin
                                    fontSize: '0.65rem' // Smaller font
                                }}>
                                    Current session
                                </Typography>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    gap: 0.5, // Reduced gap
                                    p: 0.5, // Reduced padding
                                    borderRadius: '12px',
                                    backgroundColor: 'rgba(100,180,255,0.15)',
                                    boxShadow: 'inset 0 0 6px rgba(100,180,255,0.2)',
                                    minWidth: '120px', // Smaller width
                                    justifyContent: 'center'
                                }}>
                                    <AccessTimeFilledIcon fontSize="small" sx={{ color: '#64b5f6', fontSize: '0.75rem' }}/>
                                    <Typography 
                                        variant="body2" // Changed to body2 (smaller)
                                        sx={{ 
                                            fontFamily: 'monospace', 
                                            fontWeight: 'bold',
                                            color: '#fff',
                                            textShadow: '0 0 6px rgba(100,180,255,0.8)',
                                            fontSize: '0.75rem' // Smaller font
                                        }}
                                    >
                                        {formatTime(editingStats.currentSessionTime)}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        
                        {(!editingStats.totalTimeByLevel || Object.keys(editingStats.totalTimeByLevel).length === 0) && (
                            <Box sx={{
                                p: 0.5, // Reduced padding
                                textAlign: 'center',
                                borderRadius: '4px',
                                backgroundColor: 'rgba(255,255,255,0.05)',
                            }}>
                                <HourglassEmptyIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)', mb: 0.25 }} />
                                <Typography variant="caption" color="rgba(222, 217, 217, 0.7)" sx={{ fontSize: '0.65rem' }}>
                                    Calculating ...
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
                
                {/* Video and controls wrapper */}
                <div style={{ 
                    width: '100%', 
                    flex: '1 1 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    alignItems: 'center',
                }}>
                    {/* Use VideoWrap directly, not nested within itself */}
                    <VideoWrap 
                        setPlayer={props.setPlayer} 
                        setCurrentTime={props.setCurrentTime} 
                        setPlaying={props.setPlaying} 
                    />
                    
                    {/* Subtitle container */}
                    <div 
                        className="subtitle-container"
                        style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '0 5px',
                            position: 'absolute',
                            bottom: '5%',
                            zIndex: 1000
                        }}
                    >
                        {currentSub ? (
                            <div className="subtitle" style={{ border: 'none' }}>
                                {props.viewEng || !currentSub.text2 ? (
                                    <TextareaAutosize
                                        className={`textarea`}
                                        value={currentSub.text || ''}
                                        onChange={onChangeEng}
                                        onClick={onClick}
                                        onFocus={onFocus}
                                        onBlur={onBlur}
                                        onKeyDown={onFocus}
                                        spellCheck={false}
                                        style={{
                                            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                                            fontSize: window.innerWidth < 768 ? '14px' : '16px',
                                            padding: '8px',
                                            width: '100%',
                                            minWidth: '400px',
                                            backgroundColor: 'transparent',
                                            color: 'white',
                                            border: 'none',
                                            textAlign: 'center'
                                        }}
                                    />
                                ) : (
                                    <TextareaAutosize
                                        className={`textarea`}
                                        value={currentSub.text2 || ''}
                                        onChange={onChangeEng}
                                        onClick={onClick}
                                        onFocus={onFocus}
                                        onBlur={onBlur}
                                        onKeyDown={onFocus}
                                        spellCheck={false}
                                        style={{
                                            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                                            fontSize: window.innerWidth < 768 ? '14px' : '16px',
                                            padding: '8px',
                                            width: '100%',
                                            minWidth: '400px',
                                            backgroundColor: 'transparent',
                                            color: 'white',
                                            border: 'none',
                                            textAlign: 'center'
                                        }}
                                    />
                                )}
                            </div>
                        ) : (
                           // Only show the fallback message when there are no subtitles available at all
                            // Otherwise, show nothing during gaps between subtitles
                            props.subtitle && props.subtitle.length === 0 ? (
                                <div className="subtitle-fallback" style={{ border: 'none' }}>
                                    No subtitles available
                                </div>
                            ) : null
                        )}
                    </div>
                </div>
            </div>
        </Style>
    );
}
